import { useCallback, useEffect, useState } from 'react';
import {
  InvoicesVariantRequestDto,
  InvoicesVariantResponseDto,
  ProductPrices,
  ProductWeightsAndDimensions,
} from '@tiendanube/common';
import { InterfaceNameChecked } from '@tiendanube/components';
import useScrollToError from 'App/components/AppLayout/useScrollToError';
import { CATALOG_UPLOAD_VIDEOS } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { useResponsive, Stack, VideoPlayer } from 'commons/components';
import { useAiField, useAiGeneratedValues } from 'commons/hooks';
import { AI_FIELDS } from 'commons/services/AgentChatService/AgentChatActionBusTypes';
import mapDefinedValues from 'commons/utils/mapDefinedValues';
import {
  useHasCatalogInventory,
  useHasPermission,
  useHasShippingMultiCD,
} from 'domains/Auth/hooks';
import { productHasVariants } from 'domains/Catalog/Products/utils';
import useGetProductsAndVariantsMetafields from 'domains/Metafields/hooks/ProductsAndVariants/useGetProductsAndVariantsMetafields';
import { useShowInvoiceCard, useShowNoWeightAndDimension } from '../../hooks';
import {
  HandleChangeInterface,
  ProductFormState,
  RelatedProductInterface,
} from '../../hooks/useProductForm/types';
import Categories from '../Categories';
import FreeShippingAndShowInStore from '../FreeShippingAndShowInStore';
import HighlightProduct from '../HighlightProduct';
import ImageGallery from '../ImageGallery';
import { ImageGalleryState } from '../ImageGallery/ImageGallery';
import Inventory from '../Inventory';
import { InvoiceSettingsCard } from '../InvoiceSettingsCard';
import Location from '../Location/Location';
import NameAndDescription from '../NameAndDescription';
import Prices from '../Prices';
import ProductCodes from '../ProductCodes';
import ProductSizes from '../ProductSizes';
import ProductType from '../ProductType';
import RelatedProduct from '../RelatedProduct';
import SectionProductsAndVariantsMetafieldsWrapper from '../SectionProductsAndVariantsMetafieldsWrapper';
import SocialShopping from '../SocialShopping';
import Stock from '../Stock';
import TagsAndSeo from '../TagsAndSeo';
import Variants from '../Variants';
import {
  OnChangeAttributes,
  OnChangeToAllMetafieldsVariants,
  OnChangeToAllVariants,
  Variant,
} from '../Variants/types';

interface FormProductProps {
  initialStock?: string | null;
  values: ProductFormState;
  errors: Partial<Record<string, string>>;
  isFormSubmitted: boolean;
  handleChange: ({ name, value }: HandleChangeInterface) => void;
  handleChangeCategories: (categories: string[]) => void;
  handleChangeImages: (images: ImageGalleryState[], id?: string) => void;
  handleChangeVariants: (index: number, data: Partial<Variant>) => void;
  handleChangeAttributes: OnChangeAttributes;
  handleChangeToAllVariants: OnChangeToAllVariants;
  handleChangeSectionCodes: (event: InterfaceNameChecked) => void;
  handleChangeRelatedProducts: (
    relatedProducts: RelatedProductInterface,
  ) => void;
  handleChangeToAllMetafieldsVariants: OnChangeToAllMetafieldsVariants;
  handleChangeInvoice: (index: number, data: InvoicesVariantRequestDto) => void;
  autoOpenSeoModal?: boolean;
}

function FormProduct({
  initialStock,
  values,
  errors,
  isFormSubmitted,
  handleChange,
  handleChangeCategories,
  handleChangeImages,
  handleChangeVariants,
  handleChangeAttributes,
  handleChangeToAllVariants,
  handleChangeSectionCodes,
  handleChangeToAllMetafieldsVariants,
  handleChangeRelatedProducts,
  handleChangeInvoice,
  autoOpenSeoModal = false,
}: FormProductProps): JSX.Element {
  const hasShippingMultiCD = useHasShippingMultiCD();
  const hasCatalogInventory = useHasCatalogInventory();
  const [hasUploadVideos] = useHasTags([CATALOG_UPLOAD_VIDEOS]);

  const [isVideoVisible, setIsVideoVisible] = useState(false);

  const showVideo = () => setIsVideoVisible(true);
  const hideVideo = () => setIsVideoVisible(false);

  const { isMobile } = useResponsive();

  const showInvoiceCard = useShowInvoiceCard();

  const canEditStock = useHasPermission('edit_stock');

  const { fetchMetafields } = useGetProductsAndVariantsMetafields();

  useAiField<ProductPrices>({
    componentId: AI_FIELDS.PRODUCT_PRICES,
    getValue: () => {
      const { price, promotionalPrice, costPrice, pricePublished } =
        values.variants?.[0] ?? {};
      return {
        sell_price: Number.parseInt(price) || 0,
        promotional_price: Number.parseInt(promotionalPrice) || 0,
        cost_price: Number.parseInt(costPrice) || 0,
        show_in_store: pricePublished,
      };
    },
  });

  const weightsAndDimensionsField = useAiField<ProductWeightsAndDimensions>({
    componentId: AI_FIELDS.PRODUCT_WEIGHTS_AND_DIMENSIONS,
    getValue: () => {
      const { weight, depth, width, height } = values.variants?.[0] ?? {};
      return {
        weight: Number.parseFloat(weight) || 0,
        depth: Number.parseFloat(depth) || 0,
        width: Number.parseFloat(width) || 0,
        height: Number.parseFloat(height) || 0,
      };
    },
    onReplace: (value) => {
      const variantUpdate = mapDefinedValues<
        ProductWeightsAndDimensions,
        Variant
      >(value, {
        weight: { targetKey: 'weight', transform: String },
        depth: { targetKey: 'depth', transform: String },
        width: { targetKey: 'width', transform: String },
        height: { targetKey: 'height', transform: String },
      });

      handleChangeVariants(0, variantUpdate);
    },
  });

  const weightsAndDimensionsFieldState =
    useAiGeneratedValues<ProductWeightsAndDimensions>(
      AI_FIELDS.PRODUCT_WEIGHTS_AND_DIMENSIONS,
    );

  useEffect(() => {
    fetchMetafields();
  }, [fetchMetafields]);

  const onChangeFirstVariant = useCallback(
    ({ name, value }) => {
      const variantValue = {
        [name]: value,
      } as Partial<Variant>;
      handleChangeVariants(0, variantValue);

      weightsAndDimensionsFieldState.resetAiGeneratedValues({
        overridePath: name,
      });
    },
    [handleChangeVariants, weightsAndDimensionsFieldState],
  );

  const isEdit = values.id !== undefined && values.id !== '';
  const [firstVariant] = values.variants;
  const imageVariants =
    values.images?.[0]?.publicUrl || values.images?.[0]?.src;
  const isDigital = values.isDigital;
  const hasVariants = productHasVariants(values.variants);

  const { id: variantId, weight, depth, width, height } = firstVariant;

  const showNoWeightAndDimension = useShowNoWeightAndDimension({
    variantId,
    weight,
    depth,
    width,
    height,
  });

  const altTextGenerationContext = {
    productName: values.name,
    categoryIds: values.categories,
  };

  const video = values.media.find((c) => c.mediaType === 'video');

  useScrollToError(errors);

  const normalizedInvoiceSettings: InvoicesVariantResponseDto | undefined =
    values.variants[0]?.invoiceSettings
      ? {
          id: values.variants[0].invoiceSettings.id ?? 0,
          origin: values.variants[0].invoiceSettings.origin ?? '',
          product_type: values.variants[0].invoiceSettings.product_type ?? '',
          ncm: values.variants[0].invoiceSettings.ncm ?? '',
          cest: values.variants[0].invoiceSettings.cest ?? '',
        }
      : undefined;

  return (
    <Stack column spacing="loose" align="stretch">
      <NameAndDescription
        onChange={handleChange}
        name={values.name}
        description={values.description}
        errors={errors}
        autoFocus={!isEdit}
      />
      {video && isVideoVisible && (
        <VideoPlayer videoUrl={video.videoUrl} onClose={hideVideo} />
      )}
      <ImageGallery
        isFormSubmitted={isFormSubmitted}
        images={hasUploadVideos ? values.media : values.images}
        altTextGenerationContext={altTextGenerationContext}
        isEdit={isEdit}
        errors={errors}
        videoUrl={values.videoUrl}
        onClickVideo={showVideo}
        onChange={handleChangeImages}
        onChangeExternalVideoUrl={handleChange}
      />
      {!hasVariants && (
        <Prices
          price={firstVariant.price}
          promotionalPrice={firstVariant.promotionalPrice}
          published={firstVariant.pricePublished}
          costPrice={firstVariant.costPrice}
          onChange={onChangeFirstVariant}
        />
      )}
      <ProductType onChange={handleChange} isDigital={isDigital} />
      {!hasVariants && !(hasShippingMultiCD && hasCatalogInventory) && (
        <Stock
          stock={firstVariant.stock}
          onChange={onChangeFirstVariant}
          variantId={firstVariant.id}
          variantName={firstVariant.name}
          productId={firstVariant.productId}
          isEdit={isEdit}
          locationId={firstVariant.locationId}
          canEditStock={canEditStock}
        />
      )}
      {!hasVariants && hasShippingMultiCD && hasCatalogInventory && (
        <Inventory
          stock={firstVariant.stock}
          isInfinite={firstVariant.isInfinite}
          variantId={firstVariant.id}
          variantName={firstVariant.name}
          productId={firstVariant.productId}
          locationId={firstVariant.locationId}
          variant={firstVariant}
          isEdit={isEdit}
          currentStock={initialStock}
          onChange={onChangeFirstVariant}
          hasShippingMultiCD={hasShippingMultiCD}
          hasVariants={hasVariants}
          onChangeLocation={onChangeFirstVariant}
          canEditStock={canEditStock}
        />
      )}
      {!hasVariants && (
        <ProductCodes
          appearance="row"
          sku={firstVariant.sku}
          barcode={firstVariant.barcode}
          onChange={onChangeFirstVariant}
        />
      )}
      {!hasVariants && hasShippingMultiCD && !hasCatalogInventory && (
        <Location
          locationId={firstVariant.locationId}
          isMobile={isMobile}
          hasVariants={hasVariants}
          onChange={onChangeFirstVariant}
        />
      )}
      {!hasVariants && !isDigital && (
        <ProductSizes
          weight={firstVariant.weight}
          depth={firstVariant.depth}
          width={firstVariant.width}
          height={firstVariant.height}
          showAlertNoWeightAndDimension={showNoWeightAndDimension}
          onChange={onChangeFirstVariant}
          appearance="row"
          requiredAiProperties={{
            name: values.name,
            description: values.description,
            imageUrl: imageVariants,
            variantName: firstVariant.name,
          }}
          aiGeneratedWeightsAndDimensions={
            weightsAndDimensionsFieldState.aiGeneratedValues
          }
          aiNavigationAttributes={
            weightsAndDimensionsField.navigationAttributes
          }
        />
      )}
      {!hasVariants && (
        <SocialShopping
          mpn={firstVariant.mpn}
          ageGroup={firstVariant.ageGroup}
          gender={firstVariant.gender}
          onChange={onChangeFirstVariant}
          appearance={isMobile ? 'column' : 'row'}
          onChangeToAllVariants={handleChangeToAllVariants}
        />
      )}
      <Categories
        onChange={handleChangeCategories}
        selectedCategories={values.categories}
        requiredAiProperties={{
          name: values.name,
          description: values.description,
          imageUrl: imageVariants,
        }}
      />
      <Variants
        variants={values.variants}
        attributes={values.attributes}
        productImages={values.images}
        isDigital={values.isDigital}
        defaultImage={imageVariants}
        isEdit={isEdit}
        requiredAiProperties={{
          name: values.name,
          description: values.description,
        }}
        onChange={handleChangeVariants}
        onChangeAttributes={handleChangeAttributes}
        onChangeToAllVariants={handleChangeToAllVariants}
        onChangeToAllMetafieldsVariants={handleChangeToAllMetafieldsVariants}
        onChangeInvoice={handleChangeInvoice}
        productId={values.id || ''}
      />
      <SectionProductsAndVariantsMetafieldsWrapper
        source="page"
        hasVariant={hasVariants}
        productId={values.id || ''}
        selecteds={{
          product_variant: firstVariant.metafields?.internals || [],
          product: values.productMetafields?.internals || [],
        }}
        apiSelecteds={{
          product_variant: firstVariant.metafields?.fromApi || [],
          product: values.productMetafields?.fromApi || [],
        }}
        onVariantChange={onChangeFirstVariant}
        onProductChange={handleChange}
      />
      <TagsAndSeo
        tags={values.tags}
        brand={values.brand}
        name={values.name}
        description={values.description}
        seoDescription={values.seoDescription}
        seoTitle={values.seoTitle}
        seoUrl={values.seoUrl}
        onChangeBrand={handleChange}
        onChangeSeo={handleChange}
        onChangeTags={handleChange}
        autoOpen={autoOpenSeoModal}
      />
      <HighlightProduct
        productId={values.id}
        sectionCodes={values.sectionCodes}
        onChange={handleChangeSectionCodes}
        isEdit={isEdit}
      />
      {!!values?.id && (
        <RelatedProduct
          mainProduct={values}
          relatedProducts={values.relatedProducts}
          onChange={handleChangeRelatedProducts}
        />
      )}
      {firstVariant && showInvoiceCard && !hasVariants && (
        <InvoiceSettingsCard
          productId={values.id}
          onChange={(data) => handleChangeInvoice(0, data)}
          invoiceSettings={normalizedInvoiceSettings}
          variantId={firstVariant.id}
        />
      )}
      <FreeShippingAndShowInStore
        onChange={handleChange}
        freeShipping={values.freeShipping}
        published={values.published}
        isDigital={isDigital}
      />
    </Stack>
  );
}

export default FormProduct;
